import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CircleNotch } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLE = {
  accepted_as_submitted: "bg-emerald-700 text-white",
  accepted_after_adaptation: "bg-blue-700 text-white",
  supportive_only: "bg-amber-500 text-white",
  not_acceptable: "bg-red-700 text-white",
  cannot_determine: "bg-slate-400 text-white",
};

function DimensionDetail({ productId, row, onClose }) {
  const [d, setD] = useState(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    axios.get(`${API}/acceptance/${productId}/assess/${row.best_asset_id}/${row.country_id}`)
      .then((r) => setD(r.data)).catch(() => setError(true));
  }, [productId, row]);
  const a = d?.assessments?.find((x) => x.requirement_type === row.requirement_type) || d?.assessments?.[0];
  return (
    <div data-testid="acceptance-detail" className="bg-slate-50 border-t border-slate-200 px-5 py-4">
      {error ? <p className="text-xs text-red-600">Could not load the assessment detail.</p>
        : !d ? <p className="text-xs text-slate-400"><CircleNotch size={13} className="animate-spin inline mr-1" />Assessing 12 dimensions…</p>
        : !a ? <p className="text-xs text-slate-400">No matching assessment.</p> : (
        <>
          <p className="text-[11px] font-semibold text-slate-700 mb-2">{d.asset_name} × {d.country_name} — {a.status_label}{d.is_template && <span className="ml-2 text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 bg-red-700 text-white">Template / Shell</span>}</p>
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-2">
            {a.dimensions.map((dim) => (
              <div key={dim.id} data-testid={`acceptance-dim-${dim.id}`} className="bg-white border border-slate-200 px-2.5 py-2">
                <p className="text-[10px] font-semibold text-slate-600">{dim.label}
                  <span className={`ml-1.5 text-[8px] uppercase tracking-wider font-bold px-1 py-0.5 ${dim.result === "pass" ? "bg-emerald-50 text-emerald-700" : dim.result === "adapt" ? "bg-amber-50 text-amber-700" : dim.result === "fail" ? "bg-red-50 text-red-700" : "bg-slate-100 text-slate-500"}`}>{dim.result}</span>
                </p>
                <p className="text-[10px] text-slate-400 mt-0.5">{dim.note}</p>
              </div>
            ))}
          </div>
          {a.caps.length > 0 && <div className="mt-2">{a.caps.map((c, i) => <p key={i} className="text-[10px] text-amber-700">• {c}</p>)}</div>}
          <button onClick={onClose} className="mt-2 text-[11px] text-slate-500 underline">Close</button>
        </>
      )}
    </div>
  );
}

export const CorrectedAcceptance = ({ productId }) => {
  const [d, setD] = useState(null);
  const [error, setError] = useState(false);
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(null);

  const load = useCallback(() => {
    axios.get(`${API}/acceptance/${productId}`)
      .then((r) => { setD(r.data); setError(false); })
      .catch(() => { setError(true); toast.error("Could not recalculate acceptance."); });
  }, [productId]);

  useEffect(() => { setD(null); setOpen(null); load(); }, [load]);

  return (
    <section data-testid="corrected-acceptance" className="bg-white border border-slate-200">
      <div className="px-5 py-4 border-b border-slate-200">
        <p className="font-heading font-bold text-sm tracking-tight">Corrected Acceptance & Reuse</p>
        <p className="text-xs text-slate-500 mt-1">Recalculated after duplicate removal, citation validation and corrected study classification. Five statuses per requirement — a template or global record never automatically satisfies a local submission requirement.</p>
      </div>
      {error ? (
        <p data-testid="acceptance-error" className="px-5 py-8 text-center text-xs text-red-600">Could not load the recalculated reuse. <button onClick={load} className="underline">Retry</button></p>
      ) : !d ? (
        <p data-testid="acceptance-loading" className="px-5 py-8 text-center text-xs text-slate-400"><CircleNotch size={14} className="animate-spin inline mr-1.5" />Recalculating acceptance across all requirements…</p>
      ) : (
        <>
          <div data-testid="acceptance-summary" className="grid grid-cols-2 md:grid-cols-6 divide-x divide-slate-200 border-b border-slate-200">
            {[["Requirements", d.summary.total_requirements, ""],
              ["Legacy accepted", d.summary.legacy_accepted_as_is, "text-slate-500"],
              ["Accepted as submitted", d.summary.status_counts.accepted_as_submitted, d.summary.status_counts.accepted_as_submitted > 0 ? "text-emerald-700" : "text-red-700"],
              ["After adaptation", d.summary.status_counts.accepted_after_adaptation, "text-blue-700"],
              ["Supportive only", d.summary.status_counts.supportive_only, "text-amber-600"],
              ["Duplicates removed", d.summary.duplicates_removed, d.summary.duplicates_removed > 0 ? "text-red-700" : ""]].map(([label, value, cls]) => (
              <div key={label} className="px-4 py-3">
                <p className="overline-label text-slate-400">{label}</p>
                <p className={`font-heading font-extrabold text-xl mt-0.5 ${cls}`}>{value}</p>
              </div>
            ))}
          </div>
          {d.summary.downgraded_requirements > 0 && (
            <div data-testid="acceptance-downgrade-note" className="bg-amber-50 border-b border-amber-200 px-5 py-2.5 text-[11px] text-amber-800">
              <span className="font-bold">{d.summary.downgraded_requirements} requirement(s)</span> previously counted as accepted are downgraded after applying citation validation, quality review, template rules and corrected classification.
              {d.duplicates.length > 0 && <> Duplicate removed: {d.duplicates.map((x) => `"${x.duplicate_name}" (kept "${x.kept_name}")`).join("; ")}.</>}
            </div>
          )}
          <div className="px-5 py-2.5 border-b border-slate-100 flex flex-wrap items-center gap-1.5">
            <button data-testid="acceptance-filter-all" onClick={() => setFilter("all")}
              className={`text-[10px] font-semibold px-2.5 py-1 border ${filter === "all" ? "bg-slate-900 text-white border-slate-900" : "border-slate-300 text-slate-500"}`}>All</button>
            {d.statuses.map((s) => (
              <button key={s.id} data-testid={`acceptance-filter-${s.id}`} onClick={() => setFilter(s.id)}
                className={`text-[10px] font-semibold px-2.5 py-1 border ${filter === s.id ? "bg-slate-900 text-white border-slate-900" : "border-slate-300 text-slate-500"}`}>
                {s.label} ({d.summary.status_counts[s.id]})
              </button>
            ))}
          </div>
          <div className="max-h-[420px] overflow-y-auto">
            {d.requirements.filter((r) => filter === "all" || r.corrected_status === filter).length === 0 ? (
              <p data-testid="acceptance-empty" className="px-5 py-8 text-center text-xs text-slate-400">No requirements with this status.</p>
            ) : d.requirements.filter((r) => filter === "all" || r.corrected_status === filter).map((r, i) => {
              const key = `${r.country_id}-${r.requirement_type}`;
              return (
                <div key={key} className="border-b border-slate-100 last:border-0">
                  <div data-testid={`acceptance-row-${key.replace(/[^a-zA-Z0-9-]/g, "_")}`} className="px-5 py-2.5 flex items-center gap-3">
                    <span className="shrink-0">{r.flag}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-slate-800 truncate">{r.country_name} — {r.requirement_type} <span className="text-[9px] text-slate-400 font-normal">({r.level})</span></p>
                      <p className="text-[10px] text-slate-400 truncate">{r.best_asset ? `Best asset: ${r.best_asset}` : "No matching evidence record"}{r.caps[0] && ` · ${r.caps[0]}`}</p>
                    </div>
                    <span className={`text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 shrink-0 ${STATUS_STYLE[r.corrected_status]}`}>{r.corrected_label}</span>
                    {r.legacy_status === "accepted" && r.corrected_status !== "accepted_as_submitted" && (
                      <span className="text-[8px] uppercase tracking-wider font-bold px-1 py-0.5 bg-red-50 text-red-700 border border-red-200 shrink-0">was accepted</span>
                    )}
                    {r.best_asset_id && (
                      <button data-testid={`acceptance-expand-${key.replace(/[^a-zA-Z0-9-]/g, "_")}`} onClick={() => setOpen(open === key ? null : key)}
                        className="text-[10px] text-slate-500 border border-slate-300 px-2 py-1 hover:bg-slate-50 shrink-0">12 dimensions</button>
                    )}
                  </div>
                  {open === key && <DimensionDetail productId={productId} row={r} onClose={() => setOpen(null)} />}
                </div>
              );
            })}
          </div>
        </>
      )}
    </section>
  );
};
